import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { VoucherCodeDealProps } from "~/components/VoucherCode/VoucherCodeDeal";
import type { VoucherCodeOutcomeProps } from "~/components/VoucherCode/VoucherCodeOutcome";
import type {
  LocationDeal,
  PromotionRewardType,
} from "~/graphql-client/generated";
import { createCartItem, useCart } from "~/hooks/cart";
import { useClearPath } from "~/hooks/clear-path";
import { dealToProduct, useLegacyDeals, useLocationDeals } from "~/hooks/deals";
import { useLocation } from "~/hooks/locations";
import { useBaseMenu } from "~/hooks/menu-v2";
import { useUser } from "~/hooks/user";
import { useVoucherCode } from "~/hooks/voucher-code";
import type { CartItem, OrderDispatchOption, Product } from "~/types";
import {
  dealAvailable,
  findDeal,
  findDealLegacy,
  getConflictingDeals,
  isDealAsVoucher,
  isLegacyDealAsVoucher,
  type SharedDealFields,
} from "~/utils/deal";

interface DealLegacy {
  product: Product;
  cartItemLineItemId?: string;
}

interface Deal {
  locationDeal: LocationDeal;
  cartItemLineItemId?: string;
}

type VoucherAction =
  | { type: "show" }
  | { type: "hide" }
  | { type: "value"; value: string }
  | { type: "busy" }
  | {
      type: "voucher";
      message: string;
      title: string;
      rewardType?: PromotionRewardType | null;
    }
  | { type: "error"; message: string }
  | { type: "deal-legacy"; deal: DealLegacy }
  | { type: "deal"; deal: Deal }
  | {
      type: "deal-conflict";
      deal: Deal;
      dealItem: CartItem;
      dealProduct: Product;
      dealsToRemove: SharedDealFields[];
    }
  | { type: "deal-unavailable" }
  | { type: "not-logged-in" };

interface VoucherState {
  value: string;
  error?: string;
  voucher?: {
    title: string;
    message: string;
    rewardType: PromotionRewardType | null;
  };
  show: boolean;
  dealLegacy?: DealLegacy;
  deal?: Deal;
  dealConflict?: {
    deal: Deal;
    dealItem: CartItem;
    dealProduct: Product;
    dealsToRemove: SharedDealFields[];
  };
  dealUnavailable?: boolean;
  busy?: boolean;
  notLoggedIn?: boolean;
}

const voucherReducer = (
  state: VoucherState,
  action: VoucherAction,
): VoucherState => {
  switch (action.type) {
    case "show":
      return { ...state, show: true };

    case "hide":
      return {
        ...state,
        show: false,
        busy: false,
        value: "",
        dealLegacy: undefined,
        deal: undefined,
        dealConflict: undefined,
        dealUnavailable: undefined,
        voucher: undefined,
        error: undefined,
        notLoggedIn: undefined,
      };

    case "busy":
      return { ...state, busy: true };

    case "value":
      return { ...state, value: action.value.toUpperCase() };

    // Mutually exclusive — setting one clears the other so a stale deal can't shadow it.
    case "deal-legacy":
      return {
        ...state,
        busy: false,
        dealLegacy: action.deal,
        deal: undefined,
        dealConflict: undefined,
        dealUnavailable: undefined,
      };

    case "deal":
      return {
        ...state,
        busy: false,
        deal: action.deal,
        dealLegacy: undefined,
        dealConflict: undefined,
        dealUnavailable: undefined,
      };

    case "deal-conflict":
      return {
        ...state,
        dealConflict: {
          deal: action.deal,
          dealItem: action.dealItem,
          dealProduct: action.dealProduct,
          dealsToRemove: action.dealsToRemove,
        },
        deal: undefined,
        dealLegacy: undefined,
        dealUnavailable: undefined,
        busy: false,
      };

    case "deal-unavailable":
      return {
        ...state,
        busy: false,
        dealUnavailable: true,
        deal: undefined,
        dealLegacy: undefined,
        dealConflict: undefined,
      };

    case "voucher":
      return {
        ...state,
        busy: false,
        voucher: {
          title: action.title,
          message: action.message,
          rewardType: action.rewardType ?? null,
        },
      };

    case "error":
      return { ...state, error: action.message, busy: false };

    case "not-logged-in":
      return { ...state, notLoggedIn: true };

    default:
      break;
  }
  return state;
};

/**
 * The promo code carried back from a login round trip, captured on mount and
 * then cleared from the URL.
 */
export function useInitialPromoCode(enabled = true): string | undefined {
  const searchParams = useSearchParams();
  const [initialCode] = useState(() =>
    enabled ? (searchParams.get("promo-code") ?? undefined) : undefined,
  );
  useClearPath(enabled ? ["promo-code"] : []);
  return initialCode;
}

/**
 * Owns the promo code entry state and redemption flow, so any entry point
 * (page, cart, drawer) can render its own form and place the outcome dialogs
 * with `VoucherCodeOutcome`.
 */
export function useVoucherCodeRedemption({
  locationRef,
  fulfillmentType,
  onClickVoucherContinue,
  initialCode,
}: {
  locationRef: string;
  fulfillmentType: OrderDispatchOption | null;
  onClickVoucherContinue?: () => void;
  initialCode?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, isGuest, isLoading: loadingUser } = useUser();
  const { location, loading: loadingLocation } = useLocation();
  const { redeemCode, isPending } = useVoucherCode();
  const { cart, addItem, addDealFromCode } = useCart(locationRef);
  const { menu, isLoading: loadingMenu } = useBaseMenu(fulfillmentType, null);
  const { legacyDeals } = useLegacyDeals(fulfillmentType, null);
  const { deals, loading: loadingDeals } = useLocationDeals(
    locationRef,
    fulfillmentType,
  );

  const loadingPrerequisites =
    loadingLocation || loadingDeals || loadingUser || loadingMenu;

  const [
    {
      show,
      value,
      dealLegacy,
      deal,
      dealConflict,
      dealUnavailable,
      error,
      voucher,
      busy,
      notLoggedIn,
    },
    dispatch,
  ] = useReducer(voucherReducer, {
    show: Boolean(initialCode),
    value: initialCode ?? "",
  });

  const locationTimeZone = location?.timezone;
  const isSubmittingRef = useRef(false);

  // `focus` scrolls the deal into view on the cart (this page or after
  // navigating in from the promo-code page). scroll:false so Next doesn't jump
  // to the top and fight our scroll.
  const navigateToCartWithFocus = useCallback(
    (lineItemId: string) => {
      router.replace(
        `/${locationRef}/cart?focus=${encodeURIComponent(lineItemId)}`,
        { scroll: false },
      );
    },
    [locationRef, router],
  );

  const addDealAndNavigateToCart = useCallback(
    (dealItem: CartItem, dealProduct: Product) => {
      addDealFromCode(locationRef, dealItem, dealProduct);

      dispatch({ type: "hide" });

      navigateToCartWithFocus(dealItem.lineItemId);
    },
    [addDealFromCode, locationRef, navigateToCartWithFocus],
  );

  const addVoucherAndShowModal = useCallback(
    (dealItem: CartItem, deal: Deal) => {
      addItem(locationRef, dealItem);

      dispatch({ type: "deal", deal });

      if (pathname === `/${locationRef}/cart`) {
        navigateToCartWithFocus(dealItem.lineItemId);
      }
    },
    [addItem, locationRef, navigateToCartWithFocus, pathname],
  );

  const handleSubmit = useCallback(async () => {
    if (isSubmittingRef.current) return;

    try {
      if (!value) {
        return;
      }

      if (!locationTimeZone) {
        return;
      }

      // Deals resolve to an empty list while still loading; submitting now would
      // miss a valid deal PLU and fall through to redeemCode. Wait for deals.
      if (loadingPrerequisites) {
        return;
      }

      isSubmittingRef.current = true;

      dispatch({ type: "busy" });

      if (value === "DAVEORONI") {
        /**
         * This is an easter egg.
         * The idea is to not tell anyone and see how long it takes to be discovered.
         * -- Stevo
         **/
        dispatch({ type: "deal-legacy", deal: daveDeal });
        return;
      }

      // Prefer a new deal, then fall back to a legacy one.
      const deal = findDeal(deals, cart, value);

      if (deal) {
        const conflictingDeals = getConflictingDeals(
          cart.items,
          deals,
          legacyDeals,
          deal.locationDeal.exclusive,
        );

        // A DealAsVoucher (deal with no options) has nothing to configure, so
        // add it to the cart immediately unless it's already there, and show
        // the voucher modal (its Continue returns to the menu/cart).
        if (isDealAsVoucher(deal.locationDeal)) {
          const lineItemId = deal.cartItemLineItemId;

          // Already in the cart — nothing to add, just show the modal.
          if (lineItemId) {
            dispatch({ type: "deal", deal });
            // On the cart page, scroll the voucher into view behind the modal.
            if (pathname === `/${locationRef}/cart`) {
              navigateToCartWithFocus(lineItemId);
            }
            return;
          }

          const dealProduct = dealToProduct(deal.locationDeal, []);
          const voucherItem = createCartItem(dealProduct, { isDeal: true });

          if (conflictingDeals.count >= 1) {
            dispatch({
              type: "deal-conflict",
              deal: deal,
              dealItem: voucherItem,
              dealProduct: dealProduct,
              dealsToRemove: conflictingDeals.deals,
            });

            return;
          }

          addVoucherAndShowModal(voucherItem, deal);
          return;
        }

        // A configurable deal is added to the cart (usually incomplete),
        // pooling any matching items already in the cart, and the user is sent
        // straight to the cart — no modal. Menu-driven deals are unaffected.
        const products = menu.flatMap((cat) => cat.products ?? []);

        if (!dealAvailable(deal.locationDeal, products)) {
          dispatch({ type: "deal-unavailable" });
          return;
        }

        const dealProduct = dealToProduct(deal.locationDeal, products);
        // Build empty so pooled items fill slots before deal defaults do.
        const dealItem = createCartItem(dealProduct, {
          isDeal: true,
          includeDefaults: false,
        });

        if (conflictingDeals.count >= 1) {
          dispatch({
            type: "deal-conflict",
            deal: deal,
            dealItem: dealItem,
            dealProduct: dealProduct,
            dealsToRemove: conflictingDeals.deals,
          });

          return;
        }

        addDealAndNavigateToCart(dealItem, dealProduct);

        return;
      }

      const dealLegacy = findDealLegacy(locationTimeZone, menu, cart, value);

      if (dealLegacy) {
        // A legacy DealAsVoucher is added straight to the cart (unless already
        // there). Other legacy deals are still configured on the deal page.
        if (
          isLegacyDealAsVoucher(dealLegacy.product) &&
          dealLegacy.product.available &&
          !dealLegacy.cartItemLineItemId
        ) {
          // Not flagged isDeal: addItem resolves legacy deals by product id.
          const item = createCartItem(dealLegacy.product);
          addItem(locationRef, item);
          dispatch({
            type: "deal-legacy",
            deal: { ...dealLegacy, cartItemLineItemId: item.lineItemId },
          });
          if (pathname === `/${locationRef}/cart`) {
            navigateToCartWithFocus(item.lineItemId);
          }
          return;
        }

        dispatch({ type: "deal-legacy", deal: dealLegacy });
        return;
      }

      if (!user || isGuest) {
        dispatch({
          type: "not-logged-in",
        });
        return;
      }

      const result = await redeemCode(value);

      if (result) {
        dispatch({
          type: "voucher",
          message: result.message,
          title: result.title,
          rewardType: "rewardType" in result ? result.rewardType : null,
        });
      }
    } catch (err) {
      const e = err as Error;
      dispatch({ type: "error", message: e.message });
    } finally {
      isSubmittingRef.current = false;
    }
  }, [
    value,
    locationTimeZone,
    menu,
    cart,
    deals,
    legacyDeals,
    loadingPrerequisites,
    user,
    isGuest,
    redeemCode,
    addDealAndNavigateToCart,
    addVoucherAndShowModal,
    addItem,
    navigateToCartWithFocus,
    pathname,
    locationRef,
  ]);

  const handlingAutoSubmitRef = useRef<boolean>(false);

  useEffect(() => {
    if (loadingPrerequisites || handlingAutoSubmitRef.current) {
      return;
    }

    if (initialCode) {
      handlingAutoSubmitRef.current = true;
      handleSubmit();
    }
  }, [initialCode, handleSubmit, loadingPrerequisites]);

  const handleContinue = () => {
    dispatch({ type: "hide" });
    if (onClickVoucherContinue) {
      onClickVoucherContinue();
    }
  };

  const handleRemoveConflictingDeals = () => {
    if (!dealConflict) return;

    if (isDealAsVoucher(dealConflict.deal.locationDeal)) {
      addVoucherAndShowModal(dealConflict.dealItem, dealConflict.deal);

      return;
    }

    addDealAndNavigateToCart(dealConflict.dealItem, dealConflict.dealProduct);

    return;
  };

  const handleLogin = () => {
    const next = `${pathname}?promo-code=${encodeURIComponent(value)}`;

    router.replace(
      `/login/rewards?next=${encodeURIComponent(next)}&location=${locationRef}`,
    );
  };

  // The `deal` (new) and `dealLegacy` states are mutually exclusive (handleSubmit dispatches one or the other by feature flag)
  function resolveDealProps(): Omit<
    VoucherCodeDealProps,
    "busy" | "onClose"
  > | null {
    if (dealLegacy?.product && dealLegacy.product.id !== "DAVEORONI") {
      const legacyAsVoucher = isLegacyDealAsVoucher(dealLegacy.product);
      return {
        isVoucher: legacyAsVoucher,
        plu: dealLegacy.product.plu,
        title: dealLegacy.product.title,
        description: dealLegacy.product.description,
        disabled: !dealLegacy.product.available,
        inCart: Boolean(dealLegacy.cartItemLineItemId),
        onContinue: () => {
          if (legacyAsVoucher) {
            if (pathname === `/${locationRef}/cart`) {
              dispatch({ type: "hide" });
              return;
            }
            dispatch({ type: "busy" });
            router.replace(`/${locationRef}/cart`);
            return;
          }

          if (dealLegacy.product.id) {
            dispatch({ type: "busy" });
            router.replace(
              `/${locationRef}/deal?productId=${encodeURIComponent(
                dealLegacy.product.id,
              )}`,
            );
          }
        },
      };
    }

    if (deal?.locationDeal) {
      const asVoucher = isDealAsVoucher(deal.locationDeal);
      return {
        plu: deal.locationDeal.plu,
        title: deal.locationDeal.title,
        description: deal.locationDeal.description,
        inCart: Boolean(deal.cartItemLineItemId),
        isVoucher: asVoucher,
        onContinue: () => {
          // Continue goes to the cart to show the voucher; if already on the cart, just close.
          if (asVoucher) {
            if (pathname === `/${locationRef}/cart`) {
              dispatch({ type: "hide" });
              return;
            }

            dispatch({ type: "busy" });
            router.replace(`/${locationRef}/cart`);
            return;
          }

          if (deal.locationDeal.id) {
            dispatch({ type: "busy" });
            router.replace(
              `/${locationRef}/deal?dealId=${encodeURIComponent(deal.locationDeal.id)}`,
            );
          }
        },
      };
    }

    return null;
  }

  const dealProps = resolveDealProps();

  const hide = useCallback(() => dispatch({ type: "hide" }), []);
  const showInput = useCallback(() => dispatch({ type: "show" }), []);
  const setValue = useCallback(
    (value: string) => dispatch({ type: "value", value }),
    [],
  );

  const daveProduct =
    dealLegacy?.product?.id === "DAVEORONI" ? dealLegacy.product : undefined;

  const outcome: VoucherCodeOutcomeProps = {
    code: value,
    busy,
    daveProduct,
    dealProps,
    notLoggedIn,
    error,
    voucher,
    dealConflict: dealConflict && {
      newDealTitle: dealConflict.deal.locationDeal.title,
      newDealExclusive: dealConflict.deal.locationDeal.exclusive ?? false,
      dealsToRemove: dealConflict.dealsToRemove,
    },
    dealUnavailable,
    onHide: hide,
    onContinue: handleContinue,
    onLogin: handleLogin,
    onReplaceConflictingDeals: handleRemoveConflictingDeals,
  };

  return {
    value,
    show,
    isPending,
    setValue,
    showInput,
    hide,
    submit: handleSubmit,
    outcome,
    hasOutcome: Boolean(
      daveProduct ||
        dealProps ||
        dealConflict ||
        dealUnavailable ||
        notLoggedIn ||
        error ||
        voucher,
    ),
  };
}

const daveDeal: DealLegacy = {
  product: {
    id: "DAVEORONI",
    title: "LOKE x Jet's Pizza 2024",
    available: true,
    price: { amount: 0, taxIncluded: 0 },
    tags: [],
    options: [],
    imageUrl: `/images/jets_easter_egg.jpg`,
  },
  cartItemLineItemId: undefined,
};
